import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useCallback, useRef } from 'react';

interface UseInputKeyOverrideOptions {
  /**
   * Whether the override is enabled
   */
  enabled?: boolean;
  /**
   * The key to override (e.g., 'ArrowDown')
   */
  key: string;
  /**
   * Callback to trigger when the key is pressed
   */
  onKeyPress: () => void;
}

/**
 * Hook that overrides a specified key press when an input has focus.
 * Prevents default behavior and stops propagation, then triggers a callback.
 *
 * Returns a handler function that should be attached to TextInput's onKeyPress prop.
 *
 * @example
 * ```tsx
 * const { handleKeyPress } = useInputKeyOverride({
 *   key: 'ArrowDown',
 *   onKeyPress: () => {
 *     // Focus first item in list
 *   },
 *   enabled: true,
 * });
 *
 * <TextInput onKeyPress={handleKeyPress} />
 * ```
 */
export const useInputKeyOverride = ({
  key,
  onKeyPress,
  enabled = true,
}: UseInputKeyOverrideOptions): {
  /**
   * Handler to attach to TextInput's onKeyPress prop
   */
  handleKeyPress: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
} => {
  const onKeyPressRef = useRef(onKeyPress);
  const keyRef = useRef(key);

  // Keep refs up to date
  onKeyPressRef.current = onKeyPress;
  keyRef.current = key;

  // Handler for onKeyPress prop
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (!enabled) {
        return;
      }

      if (e.nativeEvent.key === keyRef.current) {
        e.preventDefault?.();
        e.stopPropagation?.();
        onKeyPressRef.current();
      }
    },
    [enabled],
  );

  return { handleKeyPress };
};
